import styled from '@emotion/styled';

export const ActionsContainer = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 16px 16px 14px;
    margin-bottom: 1rem;
    border: 1px solid rgba(131, 83, 226, 0.1);
    border-radius: 20px;
    background:
        radial-gradient(circle at top right, rgba(131, 83, 226, 0.1), transparent 34%),
        linear-gradient(180deg, #ffffff 0%, #fcfbff 100%);
    box-shadow:
        0 10px 24px -14px rgba(15, 23, 42, 0.18),
        0 1px 3px rgba(15, 23, 42, 0.04);
    width: 100%;
    position: relative;
    z-index: 2;

    @media (max-width: 768px) {
        margin: 12px 16px 16px;
        padding: 18px 16px;
        border-radius: 20px;
        gap: 14px;
        box-shadow:
            0 14px 32px -18px rgba(15, 23, 42, 0.2),
            0 1px 3px rgba(15, 23, 42, 0.04);
    }

    @media (max-width: 480px) {
        margin: 12px 12px 16px;
        padding: 16px 14px;
    }
`;

export const ActionsButtonContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-row-gap: 8px;
    grid-column-gap: 8px;

    > button {
        height: auto;
        min-height: 40px;
        padding: 8px 12px;
        border-radius: 12px;
        border: 1px solid rgba(148, 163, 184, 0.18);
        background: rgba(255, 255, 255, 0.72);
        color: #111827;
        font-size: 14px;
        font-weight: 600;
        letter-spacing: 0;
        gap: 8px;
        backdrop-filter: blur(12px);
        transition:
            transform 0.2s ease,
            border-color 0.2s ease,
            box-shadow 0.2s ease,
            background 0.2s ease,
            color 0.2s ease;

        path {
            fill: #6d41d8;
            transition: fill 0.2s ease;
        }

        &:hover {
            transform: translateY(-1px);
            background: rgba(131, 83, 226, 0.08);
            border-color: rgba(131, 83, 226, 0.28);
            color: #6d41d8;
            box-shadow: 0 12px 24px rgba(15, 23, 42, 0.06);

            path {
                fill: #6d41d8;
            }
        }

        &:active {
            transform: translateY(0);
            background: rgba(131, 83, 226, 0.14);
            border-color: rgba(131, 83, 226, 0.36);
            color: #6d41d8;

            path {
                fill: #6d41d8;
            }
        }

        &:disabled {
            opacity: 0.55;
            cursor: not-allowed;
            transform: none;
            box-shadow: none;
        }
    }

    /* Active/selected state mirrors the eyebrow chip */
    > button[aria-current='page'] {
        background: rgba(131, 83, 226, 0.12);
        border-color: rgba(131, 83, 226, 0.36);
        color: #6d41d8;
        box-shadow: inset 0 0 0 1px rgba(131, 83, 226, 0.2);

        path {
            fill: #6d41d8;
        }
    }

    /* Destructive (red) buttons get a tinted pill */
    > button[color='red'] {
        color: #d13b3b;
        border-color: rgba(242, 33, 40, 0.22);
        background: rgba(242, 33, 40, 0.04);

        path {
            fill: #d13b3b;
        }

        &:hover {
            background: rgba(242, 33, 40, 0.1);
            border-color: rgba(242, 33, 40, 0.36);
            color: #c01f25;

            path {
                fill: #c01f25;
            }
        }

        &:active {
            background: rgba(242, 33, 40, 0.16);
            border-color: rgba(242, 33, 40, 0.48);
        }
    }
`;

/* Small green "all forms completed" indicator, rendered inline inside the
 * Client Profile button. Mirrors the green dot we use in the ClientProfile
 * sidebar so the visual language stays consistent. */
export const CompletedDot = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    min-width: 14px;
    min-height: 14px;
    margin-left: 6px;
    border-radius: 999px;
    background: #22c55e;
    color: #ffffff;
    box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.18);
    line-height: 0;

    svg {
        width: 10px;
        height: 10px;
    }
`;

export const StatusSlot = styled.div`
    width: 100%;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: stretch;

    > div {
        width: 100%;
        min-width: 0;
    }

    button {
        width: 100%;
        justify-content: space-between;
    }
`;

/* -----------------------------------------------------------------------------
 * Client-view: second ActionsContainer holds ContextGrid (case, status,
 * payment), action grid, and case popover — no inner section wrapper.
 * ---------------------------------------------------------------------------*/

export const ContextGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-row-gap: 8px;
    grid-column-gap: 8px;
`;

/* Case picker that opens a popover of the candidate's cases + a "Create new
 * case" shortcut. Uses the same pill language as the action buttons but
 * spans both columns and shows a small label + truncated value + chevron. */
export const CaseDropdownButton = styled.button<{ isActive: boolean }>`
    grid-column: 1 / -1;
    width: 100%;
    min-height: 40px;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    gap: 10px;
    border-radius: 12px;
    border: 1px solid
        ${({ isActive }) => (isActive ? 'rgba(131, 83, 226, 0.38)' : 'rgba(148, 163, 184, 0.22)')};
    background: ${({ isActive }) =>
        isActive
            ? 'linear-gradient(135deg, rgba(131, 83, 226, 0.14) 0%, rgba(131, 83, 226, 0.04) 100%)'
            : 'rgba(255, 255, 255, 0.72)'};
    color: #111827;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: ${({ isActive }) =>
        isActive
            ? '0 12px 26px -18px rgba(131, 83, 226, 0.5)'
            : '0 1px 2px rgba(15, 23, 42, 0.04)'};
    transition:
        transform 0.2s ease,
        background 0.2s ease,
        border-color 0.2s ease,
        box-shadow 0.2s ease;
    backdrop-filter: blur(12px);

    &:hover {
        transform: translateY(-1px);
        background: rgba(131, 83, 226, 0.08);
        border-color: rgba(131, 83, 226, 0.32);
        color: #6d41d8;
        box-shadow: 0 12px 24px rgba(15, 23, 42, 0.06);
    }

    &:active {
        transform: translateY(0);
        background: rgba(131, 83, 226, 0.14);
        border-color: rgba(131, 83, 226, 0.36);
    }
`;

export const CaseDropdownIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    border-radius: 10px;
    background: rgba(131, 83, 226, 0.12);
    color: #6d41d8;

    svg {
        width: 16px;
        height: 16px;
    }
`;

export const CaseDropdownBody = styled.span`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    flex: 1 1 auto;
    min-width: 0;
`;

export const CaseDropdownLabel = styled.span`
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #6b7280;
    line-height: 1;
`;

export const CaseDropdownValue = styled.span`
    width: 100%;
    color: inherit;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const CaseDropdownChevron = styled.span<{ open: boolean }>`
    display: inline-flex;
    align-items: center;
    color: #6d41d8;
    transform: rotate(${({ open }) => (open ? '180deg' : '0deg')});
    transition: transform 0.2s ease;
    flex-shrink: 0;

    svg {
        width: 14px;
        height: 14px;
    }
`;

export const CaseMenu = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px;
    min-width: 260px;
    max-width: min(360px, calc(100vw - 32px));
    border-radius: 16px;
    border: 1px solid rgba(131, 83, 226, 0.1);
    background:
        radial-gradient(circle at top right, rgba(131, 83, 226, 0.08), transparent 34%),
        linear-gradient(180deg, #ffffff 0%, #fcfbff 100%);
    box-shadow:
        0 18px 44px rgba(15, 23, 42, 0.08),
        0 2px 10px rgba(15, 23, 42, 0.04);
`;

export const CaseMenuItem = styled.button<{ selected?: boolean }>`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 10px;
    border: 0;
    border-radius: 10px;
    background: ${({ selected }) => (selected ? 'rgba(131, 83, 226, 0.12)' : 'transparent')};
    color: ${({ selected }) => (selected ? '#6d41d8' : '#111827')};
    font-family: inherit;
    font-size: 14px;
    font-weight: ${({ selected }) => (selected ? 600 : 500)};
    text-align: left;
    cursor: pointer;
    transition:
        background 0.15s ease,
        color 0.15s ease;

    &:hover {
        background: rgba(131, 83, 226, 0.08);
        color: #6d41d8;
    }

    svg {
        flex-shrink: 0;
    }
`;

export const CaseMenuItemLabel = styled.span`
    flex: 1 1 auto;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const CaseMenuCreate = styled(CaseMenuItem)`
    color: #15803d;
    font-weight: 600;

    &:hover {
        background: rgba(34, 197, 94, 0.1);
        color: #15803d;
    }
`;

export const CaseMenuDivider = styled.div`
    height: 1px;
    margin: 6px 4px;
    background: rgba(148, 163, 184, 0.18);
`;

export const CaseMenuEmpty = styled.div`
    padding: 10px;
    color: #94a3b8;
    font-size: 13px;
    font-style: italic;
    text-align: center;
`;

export const TitleWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-bottom: 2px;
`;

export const Eyebrow = styled.span`
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    padding: 4px 8px;
    border-radius: 999px;
    background: rgba(131, 83, 226, 0.1);
    color: #6d41d8;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
`;

export const Title = styled.p`
    margin: 0;
    color: #111827;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.2;
`;

export const EligibilityModalContainer = styled.div`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: auto;
    max-width: 560px;
    padding: 20px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 10px 10px rgba(0, 0, 0, 0.2);
    z-index: 10;
`;

export const OnboardingModalContainer = styled.div`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: auto;
    max-width: 446px;
    padding: 20px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 10px 10px rgba(0, 0, 0, 0.2);
    z-index: 10;
`;
